import { Link } from "@remix-run/react"
import type { MetaFunction } from "@remix-run/node"
import { Reach } from "../components/Reach"
import { WaysToListen } from "../components/WaysToListen"
// import Link from "react-anchor-link-smooth-scroll"
import { metadataMapper } from "~/utils/metaMapper"

export const handle = {
  bodyId: "sub",
}

export const meta: MetaFunction = ({ location }) => {
  const title = "Common Questions | Renewing Your Mind"
  const description =
    "Here are answers to some of our most frequently asked questions."
  const metadata = metadataMapper({
    title,
    description,
    openGraphUrl: `https://renewingyourmind.org${location.pathname}`,
  })
  return metadata
}

export default function Questions() {
  const handleAnchorClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    anchor: string,
  ) => {
    event.preventDefault() // Prevent default anchor scrolling behavior
    const el = document.getElementById(anchor)

    if (el) {
      // Scroll to the element smoothly
      el.scrollIntoView({ behavior: "smooth", block: "start" })

      // Update the URL with the new hash value
      window.history.pushState({}, "", `#${anchor}`)

      // Add the highlight class to the target element
      el.classList.add("highlight")

      // Remove the highlight class after 2 seconds
      setTimeout(() => {
        el.classList.remove("highlight")
      }, 2000)
    }
  }

  return (
    <div id="wrapper">
      <div className="container">
        <main className="col-66" id="page">
          <h1>Common Questions</h1>
          <section id="questions">
            <article id="what-are-the-ways-i-can-listen-to-renewing-your-mind">
              <h1>
                <Link
                  className="anchors"
                  to="#what-are-the-ways-i-can-listen-to-renewing-your-mind"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "what-are-the-ways-i-can-listen-to-renewing-your-mind",
                    )
                  }
                >
                  What are the ways I can listen to <em>Renewing Your Mind</em>?
                </Link>
              </h1>
              <p>
                You can listen to the program online at{" "}
                <Link to="/">RenewingYourMind.org</Link>, by downloading the{" "}
                <Link target="_blank" to="https://onelink.to/ligonier">
                  Ligonier app
                </Link>
                , or by subscribing to the podcast on{" "}
                <Link
                  target="_blank"
                  to="https://itunes.apple.com/us/podcast/renewing-your-mind-r.c.-sproul/id110916650?mt=2"
                >
                  Apple Podcasts
                </Link>
                ,{" "}
                <Link target="_blank" to="https://www.youtube.com/@RYMRadio">
                  YouTube
                </Link>
                ,{" "}
                <Link
                  target="_blank"
                  to="https://open.spotify.com/show/1Nz5qhkuLv2I1Dd9fXJDQ3?si=yrXP9bQVQDOS6Diqaj9whw"
                >
                  Spotify
                </Link>
                ,{" "}
                <Link
                  target="_blank"
                  to="https://www.iheart.com/podcast/renewing-your-mind-with-rc-sproul-29468555"
                >
                  iHeartRadio
                </Link>{" "}
                or{" "}
                <Link
                  to="https://feeds.ligonier.org/RenewingYourMind"
                  target="_blank"
                >
                  RSS
                </Link>
                . To find a radio station near you, visit our{" "}
                <Link to="/stations">Stations</Link> page.{" "}
                <em>Renewing Your Mind</em> also airs each day on{" "}
                <Link target="_blank" to="https://onelink.to/refnet">
                  RefNet
                </Link>
                . Additionally, you can listen on your phone by calling{" "}
                <Link to="tel:+15183331517">518-333-1517</Link>.
              </p>
            </article>

            <article id="where-can-i-get-this-weeks-offer-for-a-gift-of-any-amount">
              <h1>
                <Link
                  className="anchors"
                  to="#where-can-i-get-this-weeks-offer-for-a-gift-of-any-amount"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "where-can-i-get-this-weeks-offer-for-a-gift-of-any-amount",
                    )
                  }
                >
                  Where can I get this week’s offer for a gift of any amount?
                </Link>
              </h1>
              <p>
                Thank you for supporting Ligonier Ministries. You can make your
                donation by going to our{" "}
                <Link
                  to="https://gift.renewingyourmind.org"
                  target="_blank"
                  rel="noreferrer"
                >
                  Special Offer
                </Link>{" "}
                section, by calling us at{" "}
                <Link to="tel:+8004354343">800-435-4343</Link>, or by mailing a
                check to Ligonier Ministries, Regional Mail Center, PO Box
                947595, Atlanta, GA 30394-7595.
              </p>
            </article>

            <article id="can-i-hear-previous-renewing-your-mind-broadcasts-online">
              <h1>
                <Link
                  className="anchors"
                  to="#can-i-hear-previous-renewing-your-mind-broadcasts-online"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "can-i-hear-previous-renewing-your-mind-broadcasts-online",
                    )
                  }
                >
                  Can I hear previous <em>Renewing Your Mind</em> broadcasts
                  online?
                </Link>
              </h1>
              <p>
                Listen to past editions of the program by visiting our{" "}
                <Link to="/archive">Archive</Link> page.
              </p>
            </article>

            <article id="can-i-get-a-transcript-of-one-of-the-broadcasts">
              <h1>
                <Link
                  className="anchors"
                  to="#can-i-get-a-transcript-of-one-of-the-broadcasts"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "can-i-get-a-transcript-of-one-of-the-broadcasts",
                    )
                  }
                >
                  Can I get a transcript of one of the broadcasts?
                </Link>
              </h1>
              <p>
                Unfortunately, we do not have transcripts of our{" "}
                <em>Renewing Your Mind</em> broadcasts at this time.
              </p>
            </article>

            <article id="are-any-of-the-teaching-series-aired-on-renewing-your-mind-available-in-spanish-or-other-languages">
              <h1>
                <Link
                  className="anchors"
                  to="#are-any-of-the-teaching-series-aired-on-renewing-your-mind-available-in-spanish-or-other-languages"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "are-any-of-the-teaching-series-aired-on-renewing-your-mind-available-in-spanish-or-other-languages",
                    )
                  }
                >
                  Are any of the teaching series aired on{" "}
                  <em>Renewing Your Mind</em> available in Spanish or other
                  languages?
                </Link>
              </h1>
              <p>
                Several teaching series from Ligonier Ministries are available
                in Spanish. Visit Ligonier&rsquo;s{" "}
                <Link
                  target="_blank"
                  to="https://www.ligonier.org/store/collection/spanish-resources"
                >
                  online store
                </Link>{" "}
                to view these resources. Resources in{" "}
                <Link
                  target="_blank"
                  to="https://www.ligonier.org/give/outreach-projects/international-outreach"
                >
                  other languages
                </Link>{" "}
                are also available. In early 2018, we launched{" "}
                <Link target="_blank" to="https://www.renovandotumente.org">
                  <em>Renovando Tu Mente</em>
                </Link>
                , the Spanish-language version of <em>Renewing Your Mind</em>.
              </p>
            </article>

            <article id="do-you-broadcast-in-my-area">
              <h1>
                <Link
                  className="anchors"
                  to="#do-you-broadcast-in-my-area"
                  onClick={(e) =>
                    handleAnchorClick(e, "do-you-broadcast-in-my-area")
                  }
                >
                  Do you broadcast in my area?
                </Link>
              </h1>
              <p>
                Visit our <Link to="/stations">Stations</Link> page to find a
                radio station in your area.
              </p>
            </article>

            <article id="are-you-on-xm-or-sirius-radio">
              <h1>
                <Link
                  className="anchors"
                  to="#are-you-on-xm-or-sirius-radio"
                  onClick={(e) =>
                    handleAnchorClick(e, "are-you-on-xm-or-sirius-radio")
                  }
                >
                  Are you on XM or Sirius Radio?
                </Link>
              </h1>
              <p>
                No, <em>Renewing Your Mind</em> is not available on XM or Sirius
                Radio. Many people use the Bluetooth feature and listen in their
                vehicles using the{" "}
                <Link
                  to="https://www.ligonier.org/app"
                  target="_blank"
                  rel="noreferrer"
                >
                  Ligonier
                </Link>{" "}
                and{" "}
                <Link to="https://refnet.fm/" target="_blank" rel="noreferrer">
                  RefNet
                </Link>{" "}
                apps.
              </p>
            </article>

            <article id="can-i-order-the-teaching-series-that-was-on-the-program-last-week">
              <h1>
                <Link
                  className="anchors"
                  to="#can-i-order-the-teaching-series-that-was-on-the-program-last-week"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "can-i-order-the-teaching-series-that-was-on-the-program-last-week",
                    )
                  }
                >
                  Can I order the teaching series that was on the program last
                  week?
                </Link>
              </h1>
              <p>
                The teaching series that air on <em>Renewing Your Mind</em> are
                available either by calling our resource consultants at{" "}
                <Link to="tel:+8004354343">800-435-4343</Link> or by ordering
                directly from Ligonier’s{" "}
                <Link target="_blank" to="https://www.ligonier.org/store">
                  online store
                </Link>
                .
              </p>
            </article>

            <article id="how-long-has-renewing-your-mind-been-running">
              <h1>
                <Link
                  className="anchors"
                  to="#how-long-has-renewing-your-mind-been-running"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "how-long-has-renewing-your-mind-been-running",
                    )
                  }
                >
                  How long has <em>Renewing Your Mind</em> been running?
                </Link>
              </h1>
              <p>
                <em>Renewing Your Mind</em> began in 1994. Since then, it has
                aired on hundreds of stations and produced thousands of
                broadcasts. If you would like more information on the program,
                please visit our <Link to="/about">About</Link> page.
              </p>
            </article>

            <article id="what-is-the-theme-music-for-renewing-your-mind">
              <h1>
                <Link
                  className="anchors"
                  to="#what-is-the-theme-music-for-renewing-your-mind"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "what-is-the-theme-music-for-renewing-your-mind",
                    )
                  }
                >
                  What is the theme music for <em>Renewing Your Mind</em>?
                </Link>
              </h1>
              <p>
                The opening and closing music of <em>Renewing Your Mind</em> is
                &ldquo;Sinfonia&rdquo; from Handel&rsquo;s <em>Messiah</em>. The{" "}
                <em>Renewing Your Mind</em> version of &ldquo;Sinfonia&rdquo; is
                a licensed copy from a music library, which we are not able to
                sell.
              </p>
            </article>

            <article id="how-can-i-get-renewing-your-mind-broadcast-on-my-local-station">
              <h1>
                <Link
                  className="anchors"
                  to="#how-can-i-get-renewing-your-mind-broadcast-on-my-local-station"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "how-can-i-get-renewing-your-mind-broadcast-on-my-local-station",
                    )
                  }
                >
                  How can I get <em>Renewing Your Mind</em> broadcast on my
                  local station?
                </Link>
              </h1>
              <p>
                Our broadcast team would be happy to speak with you. Please{" "}
                <Link to="mailto:feedback@renewingyourmind.org">
                  contact us
                </Link>
                . Let us know your city and state as well as the local radio
                station you are inquiring for.
              </p>
            </article>

            <article id="how-can-my-radio-station-that-broadcasts-the-program-obtain-a-promotional-liner-for-an-upcoming-event">
              <h1>
                <Link
                  className="anchors"
                  to="#how-can-my-radio-station-that-broadcasts-the-program-obtain-a-promotional-liner-for-an-upcoming-event"
                  onClick={(e) =>
                    handleAnchorClick(
                      e,
                      "how-can-my-radio-station-that-broadcasts-the-program-obtain-a-promotional-liner-for-an-upcoming-event",
                    )
                  }
                >
                  How can my radio station that broadcasts the program obtain a
                  promotional &ldquo;liner&rdquo; for an upcoming event?
                </Link>
              </h1>
              <p>
                Please contact the Ligonier broadcast team directly by{" "}
                <Link to="mailto:feedback@renewingyourmind.org">
                  emailing us
                </Link>{" "}
                for details.
              </p>
            </article>

            <article id="how-can-i-sponsor-the-radio-program">
              <h1>
                <Link
                  className="anchors"
                  to="#how-can-i-sponsor-the-radio-program"
                  onClick={(e) =>
                    handleAnchorClick(e, "how-can-i-sponsor-the-radio-program")
                  }
                >
                  How can I sponsor the radio program?
                </Link>
              </h1>
              <p>
                Please{" "}
                <Link to="mailto:feedback@renewingyourmind.org">email us</Link>{" "}
                if you would like to sponsor this program on your local station.
              </p>
            </article>
          </section>
        </main>
        <aside className="col-33" id="sidebar">
          <div className="block">
            <h2>Still Have a Question?</h2>
            <Link
              className="line-btn full"
              to="mailto:feedback@renewingyourmind.org"
              data-testid="get-in-touch-questions-link"
            >
              Get in Touch
            </Link>
          </div>
          <Reach />
          <WaysToListen />
        </aside>
      </div>
    </div>
  )
}
